import { Box, Stack, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useStores } from "mobx-stores/_root-store";
import { useCallback } from "react";
import { AddButton } from "share-components/mui-wrap/button/action-button";
import { PageContentContainer } from "share-components/templates/page/page-content-container";
import { PageFilterContainer } from "share-components/templates/page/page-filter-container";
import { PageHeaderContainer } from "share-components/templates/page/page-header-container";

import { QuotationsPermission } from "commons/enum/permission";
import { Dayjs } from "dayjs";
import { DateRange } from "share-components/atomics/date-range";
import { ProductGroupSelection } from "share-components/group-selection/product-group-selection/product-group-selection";
import { ProductGroupSelectionKey } from "share-components/group-selection/product-group-selection/use-product-group-selection";
import { XAutoCompleteWithPagination } from "share-components/mui-wrap/auto-complete/x-auto-complete-with-pagination";
import { SearchButton } from "share-components/mui-wrap/button/search-button";
import { XSearchTextField } from "share-components/mui-wrap/text-field/x-search-text-field";
import { AddQuotation } from "../add-quotation/add-quotation";
import { searchQuotationNumberOptionsWithPagination } from "./quotation-number-options";
import { QuotationTable } from "./quotation-table";

export const QuotationList = observer(() => {
  const {
    quotationManagement: {
      openNew,
      getQuotations,
      pagination,
      queryParams: {
        dateRangeFilter: { fromAsDayJs, toAsDayJs, setProp: setDateRangeProps },
        name,
        dealingName,
        number,
        barcode,
        code,
        setProp: setQueryParamProp,
      },
    },
  } = useStores();
  const onSearch = useCallback(async () => {
    pagination.setProp("pageIndex", 0);
    getQuotations();
  }, [getQuotations, pagination]);
  const fromDateChangedHandler = useCallback(
    (value?: Dayjs | null) => {
      setDateRangeProps("from", value ? value.toString() : null);
    },
    [setDateRangeProps],
  );

  const toDateChangedHandler = useCallback(
    (value?: Dayjs | null) => {
      setDateRangeProps("to", value ? value.toString() : null);
    },
    [setDateRangeProps],
  );

  const setProductFilterProp = useCallback(
    (prop: ProductGroupSelectionKey, value: string) => {
      setQueryParamProp(prop as "dealingName" | "name", value);
    },
    [setQueryParamProp],
  );

  return (
    <>
      <PageHeaderContainer direction={"row"} justifyContent={"space-between"}>
        <Header></Header>
      </PageHeaderContainer>
      <Stack direction="column" spacing={2}>
        <PageFilterContainer>
          <DateRange
            label={"Ngày BG"}
            fromDate={fromAsDayJs}
            toDate={toAsDayJs}
            onFromDateChanged={fromDateChangedHandler}
            onToDateChanged={toDateChangedHandler}
          ></DateRange>

          <Box sx={{ minWidth: "200px" }}>
            <XAutoCompleteWithPagination
              label="Số phiếu"
              value={number}
              searchFunc={searchQuotationNumberOptionsWithPagination}
              searchOnFocus={true}
              searchWhenRender={false}
              onValueChanged={(v) => {
                setQueryParamProp("number", v?.value ?? "");
              }}
            />
          </Box>
        </PageFilterContainer>
        <PageFilterContainer>
          <ProductGroupSelection
            dealingNameSelected={dealingName}
            nameSelected={name}
            setSelectionProp={setProductFilterProp}
            searchWhenRender={false}
            codeSelected={code}
            visibleFields={["dealingName", "code", "name"]}
          ></ProductGroupSelection>
          <XSearchTextField
            label="Mã barcode"
            value={barcode}
            onChange={(e) =>
              setQueryParamProp("barcode", e.currentTarget.value)
            }
          />
          <SearchButton asyncAction={onSearch}></SearchButton>
        </PageFilterContainer>
      </Stack>
      <PageContentContainer>
        <QuotationTable></QuotationTable>
      </PageContentContainer>

      <>{openNew && <AddQuotation></AddQuotation>}</>
    </>
  );
});

const Header = observer(() => {
  const {
    quotationManagement: { setProp },
  } = useStores();
  return (
    <>
      <Stack>
        <Typography variant="subtitle2">Quản lý phiếu báo giá</Typography>
      </Stack>
      <Stack>
        <AddButton
          accessPermission={[QuotationsPermission.Create]}
          onClick={() => {
            setProp("openNew", true);
          }}
        >
          Thêm mới
        </AddButton>
      </Stack>
    </>
  );
});
